import { ColumnDef } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PaymentByMethodStatistic } from 'src/features/statistic/types';
import { statisticMessages } from 'src/features/statistic/utils/messages';
import { CURRENCY } from 'src/types';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import { SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import StatisticTable from 'src/registry/components/statistic/StatisticTable';
import { ICON_MAP } from 'src/utils/constants';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { paymentTypeBadgeOptions } from 'src/features/payment/utils/constants';
import DynamicColorPieChart from 'src/features/statistic/components/chart/DynamicColorPieChart';
import MapLegend from 'src/features/statistic/components/MapLegend';
import { colorPaymentMethodMapping } from 'src/features/statistic/utils/constants';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';
import ViewAllButton from 'src/components/ViewAllButton';
import { PATH } from 'src/features/navigation/utils/constants';

type Props = {
    currency: CURRENCY;
    isLoading: boolean;
    data?: PaymentByMethodStatistic[];
    filterParams?: URLSearchParams;
};
export default function WidgetPaymentByMethod({ isLoading, currency, data, filterParams }: Props) {
    const { formatMessage, formatNumber } = useIntl();
    const accessPayments = useHasPermissions([PERMISSION.ACCESS_PAYMENTS]);

    const columns: ColumnDef<PaymentByMethodStatistic>[] = [
        {
            accessorKey: 'method_name',
            header: formatMessage(statisticMessages.method),
            cell: ({ row }) => {
                return (
                    <div className="flex flex-row items-center gap-1">
                        <div
                            style={{
                                backgroundColor: colorPaymentMethodMapping[row.original.method_name],
                                width: 10,
                                height: 10,
                                borderRadius: 50,
                            }}
                        />
                        <div>{formatMessage(paymentTypeBadgeOptions[row.original.method_name].label)}</div>
                    </div>
                );
            },
        },
        {
            accessorKey: 'total_amount',
            header: formatMessage(statisticMessages.total),
            cell: ({ row }) => {
                return formatNumber(row.original.total_amount / 100, {
                    style: 'currency',
                    currency,
                });
            },
        },
        {
            accessorKey: 'transactions_count',
            header: formatMessage(statisticMessages.transactions),
            maxSize: 80,
        },
        {
            accessorKey: 'average_amount',
            header: formatMessage(statisticMessages.average),
            cell: ({ row }) => {
                return formatNumber(Number(row.original.average_amount) / 100, {
                    style: 'currency',
                    currency,
                });
            },
        },
    ];

    return (
        <GeneralBlock className="min-h-110 w-full flex-1 overflow-x-auto">
            <div className="flex items-baseline justify-between">
                <SectionBlockTitle>{formatMessage(statisticMessages.paymentsByMethod)}</SectionBlockTitle>
                {accessPayments && filterParams && (
                    <ViewAllButton route={`/${PATH.PAYMENTS}/payments?${filterParams?.toString()}`} />
                )}
            </div>

            {isLoading ? (
                <Skeleton className="h-107" />
            ) : data && data.length > 0 ? (
                <div className="flex flex-1 flex-col justify-between">
                    <div className="flex flex-col">
                        <DynamicColorPieChart
                            data={data}
                            dataKey="total_amount"
                            isLoading={isLoading}
                            currency={currency}
                            fieldName="method_name"
                        />
                        <StatisticTable
                            className="w-full"
                            title={formatMessage(statisticMessages.breakdown)}
                            data={data}
                            columns={columns as any}
                        />
                    </div>
                    <div className="mt-4 flex min-h-18 items-start justify-center">
                        <MapLegend colorMapping={colorPaymentMethodMapping} />
                    </div>
                </div>
            ) : (
                <GeneralMessageBlock
                    icon={ICON_MAP.SEARCH}
                    title={formatMessage(statisticMessages.noPaymentsFound)}
                    subtitle={formatMessage(statisticMessages.chooseDifferentPeriod)}
                />
            )}
        </GeneralBlock>
    );
}
